import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiClient } from '../api/client';
import type { Audit, WpSiteListItem } from '@audit/shared-types';

const STATUS_COLOURS: Record<string, string> = {
  complete: 'bg-green-100 text-green-700',
  partial: 'bg-orange-100 text-orange-700',
  running: 'bg-yellow-100 text-yellow-700',
  pending: 'bg-gray-100 text-gray-600',
  failed: 'bg-red-100 text-red-700',
  'not run': 'bg-gray-100 text-gray-600',
};

export default function DashboardPage() {
  const { data: sites = [], isLoading: sitesLoading } = useQuery({
    queryKey: ['sites'],
    queryFn: () => apiClient.get<WpSiteListItem[]>('/sites').then((r) => r.data),
  });

  const { data: recentAudits = [], isLoading: auditsLoading } = useQuery({
    queryKey: ['audits', 'recent'],
    queryFn: () =>
      apiClient
        .get<{ data: Audit[]; total: number }>('/audits?perPage=12')
        .then((r) => r.data.data),
  });

  const summary = useMemo(() => {
    const auditedSites = sites.filter((site) => Boolean(site.latestAuditAt));
    const sitesWithIssues = sites.filter((site) => site.siteIssueCount > 0);
    const cleanSites = sites.filter(
      (site) => site.fullyAudited && site.siteIssueCount === 0,
    );
    const runningSites = sites.filter(
      (site) => site.latestAuditStatus === 'running' || site.latestAuditStatus === 'pending',
    );
    const failedSites = sites.filter((site) => site.latestAuditStatus === 'failed');
    const neverAuditedSites = sites.filter((site) => !site.latestAuditAt);
    const totalIssues = sites.reduce((sum, site) => sum + site.siteIssueCount, 0);

    const prioritySites = [...sites]
      .sort((left, right) => {
        const leftPriority = getSitePriority(left);
        const rightPriority = getSitePriority(right);

        if (leftPriority !== rightPriority) {
          return rightPriority - leftPriority;
        }

        if (left.siteIssueCount !== right.siteIssueCount) {
          return right.siteIssueCount - left.siteIssueCount;
        }

        return getTimestamp(right.latestAuditAt) - getTimestamp(left.latestAuditAt);
      })
      .slice(0, 5);

    const recentStatusCounts = recentAudits.reduce<Record<string, number>>((counts, audit) => {
      counts[audit.status] = (counts[audit.status] ?? 0) + 1;
      return counts;
    }, {});

    const recentIssueTotals = recentAudits.reduce(
      (totals, audit) => ({
        issues: totals.issues + audit.issueCount,
        critical: totals.critical + audit.criticalCount,
        serious: totals.serious + audit.seriousCount,
        moderate: totals.moderate + audit.moderateCount,
        minor: totals.minor + audit.minorCount,
      }),
      { issues: 0, critical: 0, serious: 0, moderate: 0, minor: 0 },
    );

    return {
      auditedSites: auditedSites.length,
      cleanSites: cleanSites.length,
      failedSites: failedSites.length,
      neverAuditedSites: neverAuditedSites.length,
      prioritySites,
      recentIssueTotals,
      recentStatusCounts,
      runningSites: runningSites.length,
      sitesWithIssues: sitesWithIssues.length,
      totalIssues,
    };
  }, [recentAudits, sites]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Dashboard</h2>
        <p className="mt-2 text-sm text-gray-500">
          Focus on site coverage and current risk instead of individual audit runs.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
        <StatCard
          label="Total Sites"
          value={sitesLoading ? '…' : String(sites.length)}
        />
        <StatCard
          label="Sites Audited"
          value={sitesLoading ? '…' : `${summary.auditedSites}/${sites.length}`}
        />
        <StatCard
          label="Open Issues"
          value={sitesLoading ? '…' : String(summary.totalIssues)}
          accent
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.5fr)_minmax(320px,1fr)]">
        <section className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-700">Where To Focus Next</h3>
              <p className="text-xs text-gray-500 mt-1">
                Prioritised by failed audits, issue volume, and sites that have not been reviewed yet.
              </p>
            </div>
            <Link
              to="/sites"
              className="text-xs font-medium text-brand-600 hover:text-brand-700"
            >
              View all sites
            </Link>
          </div>

          {sitesLoading ? (
            <div className="p-6 text-sm text-gray-500">Loading site priorities…</div>
          ) : summary.prioritySites.length === 0 ? (
            <div className="p-6 text-sm text-gray-500">No sites synced yet. Go to Sites to get started.</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {summary.prioritySites.map((site) => {
                const statusLabel = getSiteBadgeStatus(site);

                return (
                  <div key={site.id} className="px-6 py-4 flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <Link
                          to={`/sites/${site.id}/review`}
                          className="text-sm font-semibold text-gray-900 hover:text-brand-700"
                        >
                          {site.name}
                        </Link>
                        <StatusBadge status={statusLabel} />
                      </div>
                      <p className="text-xs text-gray-500 mt-1 truncate">{site.url}</p>
                      <p className="text-xs text-gray-500 mt-2">
                        Last audit {site.latestAuditAt ? formatDate(site.latestAuditAt) : 'not run yet'}
                      </p>
                    </div>

                    <div className="text-right shrink-0">
                      <p className="text-lg font-bold text-gray-900">{site.siteIssueCount}</p>
                      <p className="text-xs text-gray-500">open issues</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section className="bg-white border border-gray-200 rounded-xl p-6 space-y-5">
          <div>
            <h3 className="text-sm font-semibold text-gray-700">Coverage Snapshot</h3>
            <p className="text-xs text-gray-500 mt-1">
              A quick read on audit coverage and which sites still need attention.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <MiniStatCard label="Need Attention" value={sitesLoading ? '…' : String(summary.sitesWithIssues)} tone="alert" />
            <MiniStatCard label="Clean Sites" value={sitesLoading ? '…' : String(summary.cleanSites)} tone="success" />
            <MiniStatCard label="Running / Pending" value={sitesLoading ? '…' : String(summary.runningSites)} />
            <MiniStatCard label="Never Audited" value={sitesLoading ? '…' : String(summary.neverAuditedSites)} />
          </div>

          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Recent Activity</p>
            {auditsLoading ? (
              <p className="mt-3 text-sm text-gray-500">Loading audit activity…</p>
            ) : recentAudits.length === 0 ? (
              <p className="mt-3 text-sm text-gray-500">No audits run yet.</p>
            ) : (
              <>
                <div className="mt-3 flex flex-wrap gap-2">
                  {(['complete', 'running', 'pending', 'failed'] as const)
                    .filter((status) => (summary.recentStatusCounts[status] ?? 0) > 0)
                    .map((status) => (
                      <span
                        key={status}
                        className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_COLOURS[status]}`}
                      >
                        {summary.recentStatusCounts[status]} {status}
                      </span>
                    ))}
                </div>

                <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
                  <ActivityMetric label="Audits sampled" value={String(recentAudits.length)} />
                  <ActivityMetric label="Issues found" value={String(summary.recentIssueTotals.issues)} />
                  <ActivityMetric label="Critical" value={String(summary.recentIssueTotals.critical)} />
                  <ActivityMetric label="Serious" value={String(summary.recentIssueTotals.serious)} />
                </div>
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent = false }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className={`rounded-xl border p-5 ${accent ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-gray-900 border-gray-200'}`}>
      <p className={`text-xs font-semibold uppercase tracking-wide ${accent ? 'text-blue-100' : 'text-gray-500'}`}>{label}</p>
      <p className="text-3xl font-bold mt-1">{value}</p>
    </div>
  );
}

function MiniStatCard({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: string;
  tone?: 'default' | 'success' | 'alert';
}) {
  const toneClasses = {
    default: 'border-gray-200 bg-white text-gray-900',
    success: 'border-green-100 bg-green-50 text-green-800',
    alert: 'border-orange-100 bg-orange-50 text-orange-800',
  } as const;

  return (
    <div className={`rounded-xl border p-4 ${toneClasses[tone]}`}>
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <p className="mt-2 text-2xl font-bold">{value}</p>
    </div>
  );
}

function ActivityMetric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
      <p className="mt-1 text-base font-semibold text-gray-900">{value}</p>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLOURS[status] ?? 'bg-gray-100 text-gray-600'}`}>
      {status}
    </span>
  );
}

function getSitePriority(site: WpSiteListItem) {
  if (site.latestAuditStatus === 'failed') return 4;
  if (site.siteIssueCount > 0) return 3;
  if (site.latestAuditStatus === 'complete' && !site.fullyAudited) return 2;
  if (!site.latestAuditAt) return 2;
  if (site.latestAuditStatus === 'running' || site.latestAuditStatus === 'pending') return 1;
  return 0;
}

function getSiteBadgeStatus(site: WpSiteListItem) {
  if (!site.latestAuditStatus) return 'not run';
  if (site.latestAuditStatus === 'complete') {
    return site.fullyAudited ? 'complete' : 'partial';
  }
  return site.latestAuditStatus;
}

function getTimestamp(value?: string) {
  return value ? new Date(value).getTime() : 0;
}

function formatDate(value: string) {
  return new Date(value).toLocaleDateString();
}
